import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiExternalLink, FiDollarSign, FiUsers, FiTrendingUp, FiTarget,
  FiMail, FiPhone, FiCalendar, FiBriefcase, FiArrowUpRight, FiStar,
  FiGlobe, FiMapPin, FiTag, FiActivity, FiLayers
} from 'react-icons/fi';
import { api } from '../api';
import { formatCurrency, formatDate, formatNumber } from '../utils/formatters';
import { useResearchAgent } from '../context/ResearchAgentContext';
import { useDevice } from '../context/DeviceContext';
import './InvestorDetail.css';

function InvestorDetail() {
  const { pitchbookInvestorId } = useParams();
  const navigate = useNavigate();
  const { updatePageContext, clearPageContext } = useResearchAgent();
  const { isMobile } = useDevice();
  const [investor, setInvestor] = useState(null);
  const [fullData, setFullData] = useState(null);
  const [dealsData, setDealsData] = useState({ deals: [], stats: {} });
  const [portfolioCompanies, setPortfolioCompanies] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mobile collapsible sections - DOM-based approach
  useEffect(() => {
    if (!isMobile) return;

    const sectionTitles = document.querySelectorAll('.investor-detail-page .section-title, .investor-detail-page .subsection-title');

    const handleClick = (e) => {
      const title = e.currentTarget;
      const isCollapsed = title.classList.toggle('collapsed');

      let sibling = title.nextElementSibling;
      while (sibling && !sibling.classList.contains('section-title') && !sibling.classList.contains('subsection-title')) {
        if (isCollapsed) {
          sibling.classList.add('section-collapsed');
        } else {
          sibling.classList.remove('section-collapsed');
        }
        sibling = sibling.nextElementSibling;
      }
    };

    sectionTitles.forEach(title => {
      title.addEventListener('click', handleClick);
      title.style.cursor = 'pointer';
    });

    return () => {
      sectionTitles.forEach(title => {
        title.removeEventListener('click', handleClick);
      });
    };
  }, [isMobile, investor, fullData]);

  useEffect(() => {
    fetchInvestorData();
  }, [pitchbookInvestorId]);

  useEffect(() => {
    if (pitchbookInvestorId) {
      fetchInvestorDeals();
      fetchPortfolioCompanies();
    }
  }, [pitchbookInvestorId, sortBy]);

  // Update Research Agent page context when investor data loads
  useEffect(() => {
    if (investor) {
      updatePageContext('investor-detail', {
        investor_name: investor.name,
        pitchbook_id: investor.pitchbook_investor_id,
        investor_type: investor.investor_type,
        aum: investor.aum,
        description: investor.description,
        hq_location: investor.hq_location,
        hq_country: investor.hq_country,
        hq_region: investor.hq_region,
        year_founded: investor.year_founded,
        website: investor.website,
        verticals: investor.verticals,
        total_investments: investor.total_investments,
        total_exits: investor.total_exits,
        preferred_investment_types: investor.preferred_investment_types,
      });
    }
    // Clear context when leaving the page
    return () => clearPageContext();
  }, [investor, updatePageContext, clearPageContext]);

  const fetchInvestorData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch full investor data with all 39 columns
      const fullInvestorData = await api.getInvestorsFull({
        search: pitchbookInvestorId,
        limit: 1
      });

      if (!fullInvestorData || fullInvestorData.length === 0) {
        throw new Error('Investor not found');
      }

      setInvestor(fullInvestorData[0]);
      setFullData(fullInvestorData[0]);
    } catch (err) {
      setError('Investor not found or failed to load.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestorDeals = async () => {
    try {
      const data = await api.getInvestorDeals(pitchbookInvestorId, { sort_by: sortBy, limit: 100 });
      setDealsData(data || { deals: [], stats: {} });
    } catch (err) {
      console.error('Error fetching investor deals:', err);
      setDealsData({ deals: [], stats: {} });
    }
  };

  const fetchPortfolioCompanies = async () => {
    try {
      const data = await api.getInvestorCompanies(pitchbookInvestorId, { limit: 100 });
      setPortfolioCompanies(data.companies || []);
    } catch (err) {
      console.error('Error fetching portfolio companies:', err);
      setPortfolioCompanies([]);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading investor intelligence...</p>
      </div>
    );
  }

  if (error || !investor) {
    return (
      <div className="error-container">
        <h2>⚠️ {error || 'Investor not found'}</h2>
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Search
        </button>
      </div>
    );
  }

  return (
    <div className="investor-detail-page">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <h1 className="investor-title">{investor.name}</h1>
          <div className="header-tags">
            {investor.investor_type && (
              <span className="type-tag">{investor.investor_type}</span>
            )}
            {investor.investor_status && (
              <span className="status-tag">{investor.investor_status}</span>
            )}
            {investor.ownership_status && (
              <span className="status-tag">{investor.ownership_status}</span>
            )}
          </div>
          {(investor.website || investor.year_founded || investor.hq_country) && (
            <div className="header-meta-row">
              {investor.website && (
                <a href={investor.website.startsWith('http') ? investor.website : `https://${investor.website}`}
                   target="_blank" rel="noopener noreferrer" className="header-link">
                  <FiGlobe size={14} /> {investor.website.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              )}
              {investor.year_founded && (
                <span className="header-meta-item">
                  <FiCalendar size={14} /> Est. {investor.year_founded}
                </span>
              )}
              {investor.hq_country && (
                <span className="header-meta-item">
                  <FiMapPin size={14} /> {investor.hq_country}{investor.hq_region ? ` · ${investor.hq_region}` : ''}
                </span>
              )}
            </div>
          )}
          {investor.verticals && investor.verticals.length > 0 && (
            <div className="header-verticals">
              {investor.verticals.map((v, i) => (
                <span key={i} className="vertical-tag">{v}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Highlights Section */}
      <div className="highlights-section">
        <h2 className="section-title">Highlights</h2>

        {/* Key Metrics Row */}
        <div className="highlights-row">
          {/* AUM */}
          {investor.aum && !isNaN(parseFloat(investor.aum)) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiDollarSign size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Assets Under Management</span>
                <span className="highlight-value">{formatCurrency(investor.aum)}</span>
                {investor.dry_powder && (
                  <span className="highlight-subtext">Dry Powder: {formatCurrency(investor.dry_powder)}</span>
                )}
              </div>
            </div>
          )}

          {/* Investments Count */}
          {investor.investments_count && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiUsers size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Portfolio Investments</span>
                <span className="highlight-value">{formatNumber(investor.investments_count)}</span>
                {investor.funds_open && (
                  <span className="highlight-subtext">{investor.funds_open} Funds Open</span>
                )}
              </div>
            </div>
          )}

          {/* Last Closed Fund */}
          {investor.last_closed_fund_type && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiTrendingUp size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Last Closed Fund</span>
                <span className="highlight-value">
                  {investor.last_closed_fund_size ? formatCurrency(investor.last_closed_fund_size) : 'N/A'}
                </span>
                <span className="highlight-subtext">{investor.last_closed_fund_type}</span>
                {investor.last_closed_fund_close_date && (
                  <span className="highlight-subtext">{formatDate(investor.last_closed_fund_close_date)}</span>
                )}
              </div>
            </div>
          )}

          {/* Fund Size Range */}
          {(investor.min_fund_size || investor.median_fund_size || investor.max_fund_size) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiTarget size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Fund Size Range</span>
                <span className="highlight-value">
                  {investor.median_fund_size ? formatCurrency(investor.median_fund_size) : 'N/A'}
                </span>
                <span className="highlight-subtext">Median</span>
                {investor.min_fund_size && investor.max_fund_size && (
                  <span className="highlight-subtext">
                    Range: {formatCurrency(investor.min_fund_size)} - {formatCurrency(investor.max_fund_size)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Fundraising Status */}
          {investor.most_likely_fundraising && (
            <div className="highlight-card">
              <div className="highlight-icon fundraising">
                <FiTrendingUp size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Fundraising Status</span>
                <span className="highlight-value">
                  {investor.most_likely_fundraising === 'Yes' ? '🟢 Active' : '⚪ Inactive'}
                </span>
                <span className="highlight-subtext">
                  {investor.most_likely_fundraising === 'Yes' ? 'Likely Fundraising' : 'Not Currently Fundraising'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Investor Info Section */}
      <div className="detail-content">
        {/* Left Column - Description & Contact */}
        <div className="left-column">
          {investor.description && (
            <div className="info-card">
              <h2 className="section-title">About</h2>
              <div className="info-content">
                <p className="description-text">{investor.description}</p>
              </div>
            </div>
          )}

          {/* Primary Contact */}
          {investor.primary_contact && (
            <div className="info-card">
              <h2 className="section-title">Primary Contact</h2>
              <div className="contact-info">
                <div className="contact-item">
                  <strong>{investor.primary_contact}</strong>
                  {investor.primary_contact_title && (
                    <span className="contact-title">{investor.primary_contact_title}</span>
                  )}
                </div>
                {investor.primary_contact_email && (
                  <div className="contact-item">
                    <FiMail size={16} />
                    <a href={`mailto:${investor.primary_contact_email}`}>{investor.primary_contact_email}</a>
                  </div>
                )}
                {investor.primary_contact_phone && (
                  <div className="contact-item">
                    <FiPhone size={16} />
                    <span>{investor.primary_contact_phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Details */}
        <div className="right-column">
          {/* Investor Information */}
          <div className="info-card">
            <h2 className="section-title">Investor Information</h2>
            <div className="info-grid">
              {investor.hq_location && (
                <div className="info-item">
                  <span className="info-label">Headquarters</span>
                  <span className="info-value">{investor.hq_location}</span>
                </div>
              )}
              {investor.investor_type && (
                <div className="info-item">
                  <span className="info-label">Type</span>
                  <span className="info-value">{investor.investor_type}</span>
                </div>
              )}
              {investor.investor_status && (
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className="info-value">{investor.investor_status}</span>
                </div>
              )}
              {investor.funds_open && (
                <div className="info-item">
                  <span className="info-label">Open Funds</span>
                  <span className="info-value">{investor.funds_open}</span>
                </div>
              )}
              {investor.year_founded && (
                <div className="info-item">
                  <span className="info-label">Year Founded</span>
                  <span className="info-value">{investor.year_founded}</span>
                </div>
              )}
              {investor.ownership_status && (
                <div className="info-item">
                  <span className="info-label">Ownership</span>
                  <span className="info-value">{investor.ownership_status}</span>
                </div>
              )}
            </div>
          </div>

          {/* Investment Activity */}
          {(investor.total_investments || investor.total_active_portfolio || investor.total_exits) && (
            <div className="info-card">
              <h2 className="section-title">
                <FiActivity size={20} />
                Investment Activity
              </h2>
              <div className="activity-stats-row">
                {investor.total_investments != null && (
                  <div className="activity-stat">
                    <span className="activity-stat-value">{formatNumber(investor.total_investments)}</span>
                    <span className="activity-stat-label">Total Investments</span>
                  </div>
                )}
                {investor.total_active_portfolio != null && (
                  <div className="activity-stat">
                    <span className="activity-stat-value">{formatNumber(investor.total_active_portfolio)}</span>
                    <span className="activity-stat-label">Active Portfolio</span>
                  </div>
                )}
                {investor.total_exits != null && (
                  <div className="activity-stat">
                    <span className="activity-stat-value">{formatNumber(investor.total_exits)}</span>
                    <span className="activity-stat-label">Total Exits</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Investment Preferences */}
      {(investor.preferred_investment_types || investor.preferred_industries || investor.preferred_verticals || investor.preferred_geography || investor.keywords) && (
        <div className="info-card preferences-section">
          <h2 className="section-title">
            <FiLayers size={20} />
            Investment Preferences
          </h2>
          <div className="preferences-grid">
            {investor.preferred_investment_types && (
              <div className="preference-item">
                <span className="preference-label">Investment Types</span>
                <span className="preference-value">{investor.preferred_investment_types}</span>
              </div>
            )}
            {investor.preferred_industries && (
              <div className="preference-item">
                <span className="preference-label">Industries</span>
                <span className="preference-value">{investor.preferred_industries}</span>
              </div>
            )}
            {investor.preferred_verticals && (
              <div className="preference-item">
                <span className="preference-label">Verticals</span>
                <span className="preference-value">{investor.preferred_verticals}</span>
              </div>
            )}
            {investor.preferred_geography && (
              <div className="preference-item">
                <span className="preference-label">Geography</span>
                <span className="preference-value">{investor.preferred_geography}</span>
              </div>
            )}
            {investor.keywords && (
              <div className="preference-item preference-keywords">
                <span className="preference-label">Keywords</span>
                <div className="keywords-list">
                  {investor.keywords.split(',').slice(0, 12).map((kw, i) => (
                    <span key={i} className="keyword-tag">{kw.trim()}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Portfolio Companies Section */}
      {portfolioCompanies.length > 0 && (
        <div className="info-card portfolio-section">
          <div className="portfolio-header">
            <h2 className="section-title">
              <FiBriefcase size={20} />
              Portfolio Companies
              <span className="portfolio-count">{portfolioCompanies.length} companies</span>
            </h2>
          </div>

          {/* Group by investment type */}
          {['active', 'former', 'other'].map(investorType => {
            const companies = portfolioCompanies.filter(c => c.investor_type === investorType);
            if (companies.length === 0) return null;

            return (
              <div key={investorType} className="portfolio-group">
                <h3 className="portfolio-group-title">
                  {investorType === 'active' ? 'Active Investments' :
                   investorType === 'former' ? 'Former Investments' : 'Other Investments'}
                  <span className="group-count">({companies.length})</span>
                </h3>
                <div className="portfolio-list">
                  {companies.map((company, idx) => (
                    <Link
                      key={idx}
                      to={`/company/${company.pitchbook_company_id}`}
                      className="portfolio-company-row"
                    >
                      <div className="portfolio-company-info">
                        <span className="portfolio-company-name">{company.company_name}</span>
                        {company.primary_industry_code && (
                          <span className="portfolio-company-industry">{company.primary_industry_code}</span>
                        )}
                      </div>
                      <FiArrowUpRight size={16} className="portfolio-link-icon" />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Deal History Section - Full Width */}
      <div className="info-card deals-section">
        <div className="deals-header">
          <h2 className="section-title">
            <FiBriefcase size={20} />
            Deal History
            {dealsData.stats?.total_deals > 0 && (
              <span className="deals-count">{dealsData.stats.total_deals} deals</span>
            )}
          </h2>
          {dealsData.deals.length > 0 && (
            <div className="deals-sort">
              <span className="sort-label">Sort by:</span>
              <button
                className={`sort-btn ${sortBy === 'date' ? 'active' : ''}`}
                onClick={() => setSortBy('date')}
              >
                <FiCalendar size={14} />
                Date
              </button>
              <button
                className={`sort-btn ${sortBy === 'amount' ? 'active' : ''}`}
                onClick={() => setSortBy('amount')}
              >
                <FiDollarSign size={14} />
                Amount
              </button>
            </div>
          )}
        </div>

        {/* Deal Stats Summary */}
        {dealsData.stats?.total_deals > 0 && (
          <div className="deals-stats-row">
            <div className="deal-stat">
              <span className="deal-stat-value">{dealsData.stats.total_deals}</span>
              <span className="deal-stat-label">Total Deals</span>
            </div>
            <div className="deal-stat">
              <span className="deal-stat-value">{dealsData.stats.lead_deals || 0}</span>
              <span className="deal-stat-label">As Lead</span>
            </div>
            {dealsData.stats.earliest_deal && (
              <div className="deal-stat">
                <span className="deal-stat-value">{formatDate(dealsData.stats.earliest_deal)}</span>
                <span className="deal-stat-label">First Deal</span>
              </div>
            )}
            {dealsData.stats.latest_deal && (
              <div className="deal-stat">
                <span className="deal-stat-value">{formatDate(dealsData.stats.latest_deal)}</span>
                <span className="deal-stat-label">Latest Deal</span>
              </div>
            )}
          </div>
        )}

        {/* Deals List */}
        {dealsData.deals.length > 0 ? (
          <div className="deals-list">
            {dealsData.deals.map((deal, idx) => (
              <Link
                key={idx}
                to={`/deal/${deal.pitchbook_deal_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="deal-row"
              >
                <div className="deal-row-main">
                  <div className="deal-company-info">
                    <span className="deal-company-name">
                      {deal.companies || 'Unknown Company'}
                      {deal.is_lead_investor && (
                        <span className="lead-badge">
                          <FiStar size={12} />
                          Lead
                        </span>
                      )}
                    </span>
                    <span className="deal-meta">
                      {deal.deal_type}
                      {deal.deal_type_2 && ` • ${deal.deal_type_2}`}
                      {deal.primary_industry_code && ` • ${deal.primary_industry_code}`}
                    </span>
                  </div>
                  <div className="deal-metrics">
                    {deal.deal_size_usd && (
                      <span className="deal-amount">{formatCurrency(deal.deal_size_usd)}</span>
                    )}
                    {deal.deal_date && (
                      <span className="deal-date">{formatDate(deal.deal_date)}</span>
                    )}
                  </div>
                </div>
                <FiArrowUpRight size={16} className="deal-link-icon" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="no-deals">
            <p>No deal history available in our matched dataset.</p>
            <p className="no-deals-subtext">
              This investor may have deals not yet linked to our investor database.
            </p>
          </div>
        )}
      </div>

      {/* Footer meta */}
      {(investor.view_investor_online || investor.last_updated_date) && (
        <div className="investor-footer-meta">
          {investor.last_updated_date && (
            <span className="footer-meta-item">PitchBook data updated: {formatDate(investor.last_updated_date)}</span>
          )}
          {investor.view_investor_online && (
            <a href={investor.view_investor_online} target="_blank" rel="noopener noreferrer" className="footer-link">
              <FiExternalLink size={14} /> View on PitchBook
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default InvestorDetail;
